import React, { useState, useEffect, Children, createContext, useContext } from 'react';
import Typography from '@material-ui/core/Typography';
import { Paper } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { GetHelp, InfoButton } from '../FormWidgets/Description';

import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { ExpandMore } from '../Utils/Expand';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse } from '@mui/material';

import { ConnectionStatus } from '../FormWidgets/SlaveWidgets.js';
import { IsExcludedBasedOnTypeOrProject } from '../Utils/utils';

// A remounted editor can start closed without changing ordinary-page preferences.
export const StartCollapsedContext = createContext(false);

export function useExpansionState(expandId, defaultExpanded = false) {
  const startCollapsed = useContext(StartCollapsedContext);
  const [expanded, setExpanded] = useState(() => {
    if (startCollapsed) return false;
    const saved = expandId ? localStorage.getItem(`expandable_${expandId}`) : null;
    return saved === null ? defaultExpanded : saved === 'true';
  });
  useEffect(() => {
    if (expandId && !startCollapsed) {
      localStorage.setItem(`expandable_${expandId}`, expanded.toString());
    }
  }, [expanded, expandId, startCollapsed]);
  return [expanded, setExpanded];
}

const useStyles = makeStyles((theme) => ({
  outerContainer: {
    position: 'relative',
    padding: '25px 15px',
    marginTop: '15px',
    backgroundColor: theme.palette.outerContainer.background,
    boxShadow: '0 3px 2px ' + theme.palette.innerContainer.shadow,
    '&[data-setting-group-label-hidden="true"]': {
      paddingTop: 15,
      paddingBottom: 15
    },
    [theme.breakpoints.down('xs')]: {
      padding: '12px 6px',
      marginTop: '10px'
    }
  },
  valuebox: {
    boxSizing: 'border-box',
    padding: '30px',
    paddingLeft: 'min(30px, 5%)',
    margin: '5px',
    flex: '1 1 14rem',
    minWidth: 0,
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.innerContainer.background,
    color: theme.palette.fontcolor,
    boxShadow: '0 3px 2px ' + theme.palette.innerContainer.shadow,
    [theme.breakpoints.down('md')]: {
      paddingRight: '5px'
    },
    [theme.breakpoints.down('xs')]: {
      margin: '3px',
      overflowWrap: 'anywhere',
      padding: '20px 12px',
      whiteSpace: 'normal'
    }
  },
  innerContainer: {
    padding: '25px 15px',
    marginTop: '15px',
    boxShadow: '0 3px 2px ' + theme.palette.innerContainer.shadow,
    backgroundColor: theme.palette.innerContainer2.background,
    [theme.breakpoints.down('xs')]: {
      padding: '12px 6px',
      marginTop: '10px'
    }
  },
  headline: {
    alignItems: 'center',
    display: 'flex',
    fontSize: '1.35rem',
    maxWidth: '100%',
    minWidth: 0
  },
  headlineText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  headlineIcon: {
    display: 'inline-flex',
    flex: '0 0 auto'
  },
  visuallyHiddenHeading: {
    position: 'absolute',
    top: theme.spacing(3),
    left: theme.spacing(2),
    width: 1,
    height: 1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0
  }
}));

function EllipsisHeadline({ label, helpId = label, contType, helpButton }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const displayName = t(label);
  const help = GetHelp(helpId, undefined, contType);

  return (
    <Typography variant="h5" className={classes.headline} title={displayName}>
      <span className={classes.headlineText} data-customization-heading>
        {displayName}
      </span>
      <span className={classes.headlineIcon}>
        {helpButton ?? <InfoButton content={help.content} table={help.table} img={help.img} brief={help.brief} />}
      </span>
    </Typography>
  );
}

export function SettingGroup({
  controller,
  id,
  label,
  children,
  hideLabel = false,
  visible = true,
  reference,
  status = undefined
}) {
  let { t } = useTranslation();
  const classes = useStyles();
  if (!visible) return null;

  // Check if ValueBox is excluded for this controller-type
  if (
    IsExcludedBasedOnTypeOrProject(controller, id, 'headlines') ||
    IsExcludedBasedOnTypeOrProject(controller, id, 'values')
  ) {
    return null;
  }

  // Otherwise create and return content
  if (label === undefined) label = t(id);

  const childrenArray = Children.toArray(children);
  return (
    <Paper
      component={Box}
      className={classes.outerContainer}
      data-setting-group-label-hidden={hideLabel ? 'true' : undefined}
      ref={reference}
    >
      {hideLabel ? (
        <Typography
          component="h2"
          className={classes.visuallyHiddenHeading}
          data-customization-heading
        >
          {label}
        </Typography>
      ) : status !== undefined ? (
        <span style={{ fontSize: '1.3em', display: 'flex', alignItems: 'center' }}>
          <EllipsisHeadline label={label} helpId={id} />
          <ConnectionStatus
            ahu_status={status}
            controllerStatus={controller?.connection?.status}
          />
        </span>
      ) : (
        <Box style={{ marginBottom: 10 }}>
          <EllipsisHeadline label={label} helpId={id} />
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>{childrenArray}</Box>
    </Paper>
  );
}

// used for co2, ventilation, lights, alarms, advanced, scheduling
export function SettingGroupGrid({
  controller,
  id,
  label,
  subtitle,
  children,
  fullWidth,
  outside = false,
  helpButton,
  headerAction,
  compact = false
}) {
  let { t } = useTranslation();
  const classes = useStyles();
  // Check if ValueBox is excluded for this controller-type
  if (
    IsExcludedBasedOnTypeOrProject(controller, id, 'headlines') ||
    IsExcludedBasedOnTypeOrProject(controller, id, 'values')
  ) {
    return null;
  }

  // Otherwise create and return content
  if (label === undefined) label = t(id);

  const childrenArray = Children.toArray(children).flatMap((child, childIndex) => {
    const parentKey = child.key ?? childIndex;
    if (child.type !== React.Fragment) {
      return [{ element: child, key: parentKey }];
    }

    return Children.toArray(child.props.children).map((fragmentChild, fragmentChildIndex) => ({
      element: fragmentChild,
      key: `${parentKey}:${fragmentChild.key ?? fragmentChildIndex}`
    }));
  });
  return (
    <Paper
      component={Box}
      className={outside ? classes.innerContainer : classes.outerContainer}
      style={{ width: '100%', ...(compact ? { paddingTop: 8, paddingBottom: 8 } : {}) }}
    >
      <Box style={{ paddingBottom: compact ? 0 : 8 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box minWidth={0}>
            <EllipsisHeadline label={label} helpId={id} helpButton={helpButton} />
          </Box>
          {headerAction && <Box flexShrink={0} ml={1}>{headerAction}</Box>}
        </Box>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Grid container spacing={1} columns={{ xs: 6, md: 12 }}>
        {childrenArray.map(({ element, key }) => (
          <Grid key={key} item xs={12} md={fullWidth ? 12 : 6}>
            {element}
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

export function SettingGroupBlock({ id, label, children }) {
  const { t } = useTranslation();
  const childrenArray = Children.toArray(children);
  const classes = useStyles();
  return (
    <Paper
      p={{ xs: 2, sm: 4 }}
      mt={{ xs: 2, sm: 4 }}
      component={Box}
      className={classes.outerContainer}
    >
      <EllipsisHeadline label={label ?? t(id)} helpId={id} />
      <Box>
        {childrenArray.map((child, index) => (
          <Box key={child.key ?? index}> {child} </Box>
        ))}
      </Box>
    </Paper>
  );
}

export function ValueBox({ className = '', style, children, controller, id }) {
  const classes = useStyles();
  // Check if ValueBox is excluded for this controller-type
  if (IsExcludedBasedOnTypeOrProject(controller, id)) {
    return null;
  }
  // Otherwise create and return content
  const childrenArray = Children.toArray(children);
  return (
    <Paper className={`${classes.valuebox} ${className}`} style={style}>
      {childrenArray}
    </Paper>
  );
}

export function ExpandableSettingsGroupGrid({
  expand_id,
  expand_on_load = false,
  label,
  children,
  outside = false,
  spacing = 1,
  xs = 12,
  md = 6
}) {
  const [expand, setExpand] = useExpansionState(expand_id, expand_on_load);

  const handleExpandClick = () => {
    setExpand(!expand);
  };

  const childrenArray = Children.toArray(children);

  return (
    <SettingGroupGrid label={label} fullWidth={true} outside={outside}>
      <div style={{ textAlign: 'center', marginTop: '-2em' }}>
        <ExpandMore
          expand={expand}
          onClick={() => handleExpandClick()}
          aria-expanded={expand}
          aria-label="show more"
        >
          <ExpandMoreIcon fontSize="large" />
        </ExpandMore>
      </div>
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <Grid container spacing={spacing} columns={{ xs: 6, md: 12 }}>
          {childrenArray
            .filter((child) => !child.props.outside)
            .map((child) => (
              <Grid key={child.key} item xs={xs} md={md}>
                {child}
              </Grid>
            ))}
        </Grid>

        {childrenArray.filter((child) => child.props.outside).map((child) => child)}
      </Collapse>
    </SettingGroupGrid>
  );
}

export function ExpandableSettingsGroup({
  controller,
  id,
  expand_id,
  expand_on_load = false,
  label,
  children,
  outside = false,
  inlineHeader = false
}) {
  const [expand, setExpand] = useExpansionState(expand_id, expand_on_load);

  const handleExpandClick = () => {
    setExpand(!expand);
  };

  const childrenArray = Children.toArray(children);

  const expandButton = (
    <ExpandMore
      expand={expand}
      onClick={handleExpandClick}
      aria-expanded={expand}
      aria-label="show more"
      style={inlineHeader ? { width: 44, height: 44, color: 'inherit' } : undefined}
    >
      <ExpandMoreIcon fontSize="large" />
    </ExpandMore>
  );

  return (
    <SettingGroupGrid controller={controller} id={id} label={label} fullWidth={true} outside={outside}
      headerAction={inlineHeader ? expandButton : undefined} compact={inlineHeader && !expand}>
      {!inlineHeader && <div style={{ textAlign: 'center', marginTop: '-2em' }}>{expandButton}</div>}
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <Box>
          {childrenArray
            .filter((child) => !child.props.outside)
            .map((child, idx) => (
              <Box key={idx}>{child}</Box>
            ))}
        </Box>

        {childrenArray
          .filter((child) => child.props.outside)
          .map((child) => (
            <>{child}</>
          ))}
      </Collapse>
    </SettingGroupGrid>
  );
}
